import { mapGetters } from 'vuex'
import dayjs from 'dayjs'

export default {
  computed: {
    ...mapGetters('encounters', [
      'opNoteBloodLosses',
    ]),
  },
  methods: {
    getBoneLossById(id) {
      return this.opNoteBloodLosses[this.opNoteBloodLosses.findIndex(x => x.id === id)].title
    },
    generateFinalNoteBody() {
      const a1cLevel = this.encounter.a1c_level
      const dfuRisk = this.encounter.dfu_risk
      const dfuRiskReason = this.encounter.dfu_risk_reason
      const encounterId = this.encounter.id
      const footwearEvaluation = this.encounter.footwear_evaluation
      const footwearEvaluationReason = this.encounter.footwear_evaluation_reason
      const footwearExamReason = this.encounter.footwear_exam_reason
      const footwearNotableIssues = this.encounter.footwear_notable_issues
      const footwearEvaluationDate = null
      let finalNoteBody = ' <br><br>' // Space is added at the start so it doesn't remove the line feeds
      if (dfuRisk || footwearEvaluation || a1cLevel) {
        if (a1cLevel) {
          finalNoteBody += '<b>ANNUAL A1c REPORTING:</b><br>'
          if (a1cLevel === 'not performed') {
            finalNoteBody += 'Hemoglobin A1c level was not performed during the measurement period (12 months).  '
          } else {
            finalNoteBody += `Most recent hemoglobin A1c (HbA1c) level: ${a1cLevel}`
          }
        }
        if (dfuRisk === 'informed') {
          finalNoteBody += '<br><br><b>ANNUAL DFU RISK EDUCATION:</b>><br>'
          finalNoteBody += `${this.$custom.randomString(['The patient was counseled on the risks that could lead to development of diabetic foot ulcers',
            'Patient was counseled on the risks of developing diabetic foot ulcers',
            'Patient was educated with regard to factors that increase the risk of developing a diabetic foot ulcer',
            'Patient was briefed on issues that can increase the risks of developing a diabetic foot ulcer'])} on ${this.formatDate(this.encounter.visit_date)}.  `
        } else if (dfuRisk === 'not accepted') {
          finalNoteBody += '<br><br><b>DFU RISK EDUCATION:</b><br>'
          finalNoteBody += 'Patient was unable to accept diabetic foot'
          finalNoteBody += ` ulcers risk education on ${this.formatDate(this.encounter.visit_date)}. ${dfuRiskReason}.`
        }

        if (footwearEvaluation) {
          if (dfuRisk) {
            finalNoteBody += '<br><br>'
          }
          finalNoteBody += '<b>ANNUAL DFU FOOTWEAR &amp; RISK EVALUATION:</b><br>'
          if (footwearEvaluationDate) {
            finalNoteBody += `Evaluation on: ${footwearEvaluationDate}.<br>`
          }
          if (footwearEvaluation === 'not eligible') {
            finalNoteBody += 'Clinician documented that the patient was not an eligible candidate for footwear evaluation measure.  '
          } else if (footwearEvaluation === 'exam performed') {
            if (this.$custom.itemTitleContains(footwearNotableIssues, 'None')) {
              if (footwearEvaluationDate) {
                finalNoteBody += `Evaluation on ${
                  footwearEvaluationDate
                } revealed no specific issues related to inappropriate footwear and DFU risk.`
              } else {
                finalNoteBody += 'No specific issues related to inappropriate footwear and DFU risk were noted.  '
              }
            } else {
              finalNoteBody += `${
                this.$custom.toProperCase(
                  this.$custom.toListCommaAnd(footwearNotableIssues),
                )} ${this.$custom.randomString(['factors that increase risk for diabetic foot ulcer (DFU) related to footwear were noted.  ',
                'factors contributing to diabetic foot ulcer (DFU) risk related to footwear were noted.  ',
                'factors that contribute to diabetic foot ulcers (DFU) related to footwear were observed.  ',
                'issues that are related to diabetic foot ulcer (DFU) risk related to footwear were seen.  ',
                'issues that are related to increased risk for diabetic foot ulcers (DFU) related to footwear were seen.  '])}`
            }
            if (footwearExamReason === 'Footwear change recommended') {
              if (this.$custom.itemTitleContains(footwearNotableIssues, 'None')) {
                finalNoteBody += `${this.$custom.randomString([
                  'A change of footwear is nevertheless recommended.  ',
                  'A change of footwear is recommended as a preventive measure.  ',
                ])}`
              } else {
                finalNoteBody += `${this.$custom.randomString([
                  'Examination indicated signs of inappropriate footwear. A change for better fit is recommended.  ',
                  'A footwear change is recommended.  ',
                  'Upon inspection, inappropriate footwear was observed and a change is recommended.  ',
                  'Provider recommends a footwear change to reduce risk of DFU.  ',
                  'Inappropriate footwear was observed and a change is recommended to reduce risk of DFU.  ',
                ])}`
              }
            }
          }
          if (footwearEvaluation === 'not accepted') {
            finalNoteBody += `Reason: ${footwearEvaluationReason}`
          }
        }
      }
      if (this.patient.wounds) {
        this.patient.wounds.forEach(wound => {
          const treatment = wound.treatments[wound.treatments.findIndex(
            x => x.encounter_id === encounterId && x.deleted !== true,
          )]
          if (treatment === undefined) {
            return
          }
          finalNoteBody += '<br><br><hr>'
          const procedureId = treatment ? treatment.procedure_id : null
          const woundTreatment = wound.practice_type_id === 1
            ? treatment.wound_treatment : null
          const gTubeTreatment = wound.practice_type_id === 5 && treatment
            ? treatment.g_tube_treatment : null
          const woundTreatmentWoundSize = woundTreatment
            ? woundTreatment.wound_treatment_wound_size : null
          const dermTreatment = treatment && treatment.derm_treatment
            ? treatment.derm_treatment : null
          const woundStage = woundTreatment
            ? treatment.wound_treatment?.wound_treatment_wound_size?.wound_stage ?? null : null
          let preDebrideLength = null
          let preDebrideWidth = null
          let preDebrideDepth = null
          let preDebrideArea = null
          let postDebrideLength = null
          let postDebrideWidth = null
          let postDebrideDepth = null
          let postDebrideArea = null
          let postDebridePercent = null
          let postDebrideAreaDebride = null
          if (woundTreatmentWoundSize) {
            preDebrideLength = woundTreatmentWoundSize.primary_length ?? '--'
            preDebrideWidth = woundTreatmentWoundSize.primary_width ?? '--'
            preDebrideDepth = woundTreatmentWoundSize.primary_depth ?? '--'
            preDebrideArea = woundTreatmentWoundSize.primary_area ?? '--'
            postDebrideLength = woundTreatmentWoundSize.post_debridement_length ?? '--'
            postDebrideWidth = woundTreatmentWoundSize.post_debridement_width ?? '--'
            postDebrideDepth = woundTreatmentWoundSize.post_debridement_depth ?? '--'
            postDebrideArea = woundTreatmentWoundSize.post_debridement_area ?? '--'
            postDebridePercent = woundTreatmentWoundSize.post_debridement_percent ? `${woundTreatmentWoundSize.post_debridement_percent}%` : '--'
            postDebrideAreaDebride = woundTreatmentWoundSize.post_debridement_area_debrided ?? '--'
          }
          const isDebrided = (procedureId >= 100
              && procedureId <= 142)
              || procedureId === 160
          finalNoteBody += `<b>${
            this.$store.getters['baseData/practiceTypeFromId'](
              wound.practice_type_id,
            ).title?.toUpperCase()}</b>`
          if (wound.practice_type_id === 1 || wound.practice_type_id === 6) {
            finalNoteBody += `<br><br><b>LOCATION:</b> ${
              wound.location?.location_text}<br><br>`
            finalNoteBody += `<b>ETIOLOGY:</b> ${treatment.etiology === 'Other' ? treatment.etiology_other : treatment.etiology}${woundStage ? ` - Wound Stage: ${woundStage}` : ''}`
          }
          if (wound.practice_type_id === 6) {
            if (dermTreatment) {
              if (dermTreatment.lesion_conditions) {
                finalNoteBody += '<br><br><b>LESION CONDITION:</b><br>'
                finalNoteBody += `${
                  this.$custom.toListComma(dermTreatment.lesion_conditions, dermTreatment.lesion_conditions_other, 'none')
                }`
              }
              if (dermTreatment.lesion_description) {
                finalNoteBody += '<br><br><b>LESION DESCRIPTION:</b><br>'
                finalNoteBody += `${
                  dermTreatment.lesion_description
                }`
              }
              if (dermTreatment.duration) {
                finalNoteBody += '<br><br><b>DURATION:</b><br>'
                finalNoteBody += `${
                  dermTreatment.duration
                }`
              }
            }
          }
          if (wound.practice_type_id === 1) {
            if (this.$custom.notEmpty(woundTreatment?.preoperative_indications)) {
              finalNoteBody += '<br><br><b>PREOPERATIVE INDICATIONS:</b><br>'
              const preopIndicationsOtherText = woundTreatment.preoperative_indications.some(x => x.title === 'Other')
                ? woundTreatment.preoperative_indication_other
                : null
              finalNoteBody += `${
                this.$custom.toListCommaAnd(
                  woundTreatment.preoperative_indications,
                  preopIndicationsOtherText,
                )
              }`
            }
            if (woundTreatment?.infection_signs) {
              finalNoteBody += '<br><br><b>SIGNS OF INFECTION:</b><br>'
              finalNoteBody += `${
                this.$custom.toListCommaAnd(woundTreatment.infection_signs)}`
            }
          }
          if (wound.practice_type_id === 5) {
            if (gTubeTreatment) {
              if (gTubeTreatment.abdominal_exam) {
                finalNoteBody += '<br><br><b>ABDOMINAL EXAM:</b><br>'
                finalNoteBody += `${
                  gTubeTreatment.abdominal_exam === 'Other'
                    ? gTubeTreatment.abdominal_exam_other
                    : gTubeTreatment.abdominal_exam
                }`
              }
              if (gTubeTreatment.g_tube_type) {
                finalNoteBody += '<br><br><b>G-TUBE TYPE:</b><br>'
                finalNoteBody += `${
                  gTubeTreatment.g_tube_type === 'Other'
                    ? gTubeTreatment.g_tube_type_other
                    : gTubeTreatment.g_tube_type
                }`
              }
              if (gTubeTreatment.peri_tube_finding) {
                finalNoteBody += '<br><br><b>PERI-TUBE FINDING:</b><br>'
                finalNoteBody += `${
                  gTubeTreatment.peri_tube_finding
                }`
              }
              if (gTubeTreatment.duration) {
                finalNoteBody += '<br><br><b>DURATION:</b><br>'
                finalNoteBody += `${
                  gTubeTreatment.duration
                }`
              }
              if (gTubeTreatment.peri_tube_tract) {
                finalNoteBody += '<br><br><b>PERI-TUBE TRACT:</b><br>'
                finalNoteBody += `${
                  gTubeTreatment.peri_tube_tract
                }`
              }
            }
          }
          if (procedureId) {
            finalNoteBody += '<br><br><b>PROCEDURE PERFORMED:</b><br>'
            finalNoteBody += `${
              this
                .$store
                .getters['baseData/procedureFromId'](procedureId)
                .replace(/\[\d+\]/, '')
            }`
          }

          // Does not apply to ATG (Amniotic Tissue Grafts).
          if (procedureId !== 178) {
            // post_debridement_area_debrided
            const areaDebrided = woundTreatment
              ? treatment.wound_treatment?.wound_treatment_wound_size?.post_debridement_area_debrided
              ?? null : null
            if (this.$custom.notEmpty(areaDebrided) && Number(areaDebrided) > 0) {
              finalNoteBody += `  <u>A total area of ${
                areaDebrided
              } cm² of devitalized tissue was debrided</u>`
            }

            const estimatedBloodLossId = treatment
              .operative_note_wizard ? treatment
              .operative_note_wizard
              .estimated_blood_loss_id : null

            if (procedureId !== 999 && estimatedBloodLossId) {
              if (estimatedBloodLossId > 1) {
                finalNoteBody += `  with ${
                  this.getBoneLossById(estimatedBloodLossId)
                } cc estimated blood loss.  `
              } else {
                finalNoteBody += `${isDebrided ? '.  There was ' : ' with '} no loss of blood.  `
              }
            }
            if (woundTreatmentWoundSize) {
              if (isDebrided) {
                finalNoteBody += `  The pre-op wound area was ${this.$custom.randomString([
                  'found to be ',
                  'measured ',
                  'evaluated to be ',
                  'estimated to be ',
                  'measured at ',
                  'found to be ',
                  'evaluated to be ',
                ])}  ${
                  preDebrideLength || ''} cm x ${
                  preDebrideWidth || ''} cm x ${
                  preDebrideDepth || ''} cm (${
                  preDebrideArea || ''} cm²).  `
                finalNoteBody += `The post-op wound area was ${this.$custom.randomString([
                  'found to be ',
                  'measured ',
                  'evaluated to be ',
                  'estimated to be ',
                  'measured at ',
                  'found to be ',
                  'evaluated to be ',
                ])}${
                  postDebrideLength || ''} cm x ${
                  postDebrideWidth || ''} cm x ${
                  postDebrideDepth || ''} cm (${
                  postDebrideArea || ''} cm²).  `
              }
            }
          }

          if (procedureId === 999 && woundTreatment
            && woundTreatment.debridement_not_performed_reasons.length > 0
          ) {
            finalNoteBody += '<br><br><b>REASON FOR NO DEBRIDEMENT:</b><br>'
            const otherText = woundTreatment.debridement_not_performed_reasons.some(x => x.title === 'Other')
              ? woundTreatment.debridement_not_performed_reason_other
              : null
            finalNoteBody += `${
              this.$custom.toListCommaAnd(
                woundTreatment.debridement_not_performed_reasons,
                otherText,
              )
            }`
          }

          // part m
          if (wound.practice_type_id === 5 && gTubeTreatment) {
            if (gTubeTreatment.replacement_tube_type) {
              finalNoteBody += '<br><br><b>REPLACEMENT TUBE TYPE:</b><br>'
              finalNoteBody += `${
                gTubeTreatment.replacement_tube_type === 'Other'
                  ? gTubeTreatment.replacement_tube_type_other
                  : gTubeTreatment.replacement_tube_type
              }`
            }
            if (gTubeTreatment.replacement_size) {
              finalNoteBody += '<br><br><b>SIZE (French):</b><br>'
              finalNoteBody += `${gTubeTreatment.replacement_size}`
            }
            if (gTubeTreatment.balloon_cc) {
              finalNoteBody += '<br><br><b>BALLOON:</b><br>'
              finalNoteBody += `${gTubeTreatment.balloon_cc}`
            }
            if (gTubeTreatment.replacement_reason) {
              finalNoteBody += '<br><br><b>REASON FOR REPLACEMENT:</b><br>'
              finalNoteBody += `${
                gTubeTreatment.replacement_reason === 'Other'
                  ? gTubeTreatment.replacement_reason_other
                  : gTubeTreatment.replacement_reason
              }`
            }
            if (gTubeTreatment.verification_of_placement_1
            || gTubeTreatment.verification_of_placement_2
            || gTubeTreatment.verification_of_placement_3
            || gTubeTreatment.verification_of_placement_other
            ) {
              finalNoteBody += '<br><br><b>VERIFICATION OF PLACEMENT:</b><br>'
              finalNoteBody += `${
                this.$custom.toListComma(
                  [
                    gTubeTreatment.verification_of_placement_1,
                    gTubeTreatment.verification_of_placement_2,
                    gTubeTreatment.verification_of_placement_3,
                    gTubeTreatment.verification_of_placement_other,
                  ],
                )
              }`
            }
            if (procedureId !== 999 && treatment.operative_note) {
              finalNoteBody += '<br><br><b>PROCEDURAL NOTE:</b><br>'
              finalNoteBody += `${this.$custom.textAreaHtml(treatment.operative_note)}`
            }
          }
          if (woundTreatment || dermTreatment) {
            if (woundTreatment) {
              if (woundTreatment.dressing_used_1
              || woundTreatment.dressing_used_2
              || woundTreatment.dressing_used_other
              ) {
                finalNoteBody += '<br><br><b>DRESSING USED:</b><br>'
                finalNoteBody += `${
                  this.$custom.toListCommaAnd(
                    [
                      woundTreatment.dressing_used_1,
                      woundTreatment.dressing_used_2,
                      woundTreatment.dressing_used_other,
                    ].filter(x => x !== null), null, 'none',
                  )
                }`

                if ([
                  woundTreatment.dressing_used_1,
                  woundTreatment.dressing_used_2,
                  woundTreatment.dressing_used_other,
                ].filter(x => x !== null && x?.toLowerCase().includes('dakin')).length > 0) {
                  finalNoteBody += '<br><br><b>REASON FOR USING DAKIN\'S SOLUTION:</b>><br>'
                  const dakinSolutionReason = woundTreatment.reason_for_dakins_solution
                  finalNoteBody += `${dakinSolutionReason}`
                }
              }
              if (woundTreatment.wound_description_summary) {
                finalNoteBody += '<br><br><b>WOUND DESCRIPTION:</b><br>'
                finalNoteBody += `${this.$custom.textAreaHtml(woundTreatment.wound_description_summary)}`
              }
              if (woundTreatmentWoundSize) {
                finalNoteBody += '<br><br><b>TISSUE TYPES BY PERCENTAGE:</b><br>'
                finalNoteBody += `${
                  woundTreatmentWoundSize.slough || '0'
                }% slough, ${
                  woundTreatmentWoundSize.granulation || '0'
                }% granulation, ${
                  woundTreatmentWoundSize.necrotic_tissue || '0'
                }% necrotic tissue, ${
                  woundTreatmentWoundSize.hyper_granulation || '0'
                }% hypergranulation tissue, ${
                  woundTreatmentWoundSize.eschar || '0'
                }% eschar, ${
                  woundTreatmentWoundSize.epithelial || '0'
                }% epithelial tissue, ${
                  woundTreatmentWoundSize.dermal || '0'
                }% dermal `
              }
              finalNoteBody += '<br><br><b>SIZE:</b><br>'
              if (postDebrideLength === '--') {
                // Simplified single-row table
                finalNoteBody += `
                  <div data-section="PRE_WSIZE_TABLE_START"></div>
                  <table width="60%" border="1" cellspacing="0">
                      <tr>
                          <td style="padding:6px">Length (cm)</td>
                          <td style="padding:6px">Width (cm)</td>
                          <td style="padding:6px">Depth (cm)</td>
                          <td style="padding:6px">Wound Area (cm<sup>2</sup>)</td>
                      </tr>
                      <tr>
                          <td style="padding:6px">${preDebrideLength}</td>
                          <td style="padding:6px">${preDebrideWidth}</td>
                          <td style="padding:6px">${preDebrideDepth}</td>
                          <td style="padding:6px">${preDebrideArea}</td>
                      </tr>
                  </table>
                  <div data-section="PRE_WSIZE_TABLE_END"></div>`
              } else {
                // Full table with PRIMARY and POST-OP rows
                finalNoteBody += `
                  <div data-section="POST_WSIZE_TABLE_START"></div>
                  <table width="100%" border="1" cellspacing="0">
                      <tr>
                          <td></td>
                          <td style="padding:6px">Length (cm)</td>
                          <td style="padding:6px">Width (cm)</td>
                          <td style="padding:6px">Depth (cm)</td>
                          <td style="padding:6px">Area (cm<sup>2</sup>)</td>
                          <td style="padding:6px">Debrided %</td>
                          <td style="padding:6px">Debrided Area (cm<sup>2</sup>)</td>
                      </tr>
                      <tr>
                          <td style="padding:6px">PRIMARY</td>
                          <td style="padding:6px">${preDebrideLength}</td>
                          <td style="padding:6px">${preDebrideWidth}</td>
                          <td style="padding:6px">${preDebrideDepth}</td>
                          <td style="padding:6px">${preDebrideArea}</td>
                          <td style="padding:6px">--</td>
                          <td style="padding:6px">--</td>
                      </tr>
                      <tr>
                          <td style="padding:6px">POST-OP</td>
                          <td style="padding:6px">${postDebrideLength}</td>
                          <td style="padding:6px">${postDebrideWidth}</td>
                          <td style="padding:6px">${postDebrideDepth}</td>
                          <td style="padding:6px">${postDebrideArea}</td>
                          <td style="padding:6px">${postDebridePercent}</td>
                          <td style="padding:6px">${postDebrideAreaDebride}</td>
                      </tr>
                  </table>
                  <div data-section="POST_WSIZE_TABLE_END"></div>`
              }
              if (woundTreatment.progress) {
                finalNoteBody += '<br><b>WOUND PROGRESS:</b><br>'
                finalNoteBody += `${woundTreatment.progress === 'Other' && woundTreatment.progress_other ? woundTreatment.progress_other : woundTreatment.progress || ''}`
              }

              if (treatment.operative_note) {
                finalNoteBody += '<br><br><b>OPERATIVE NOTE:</b><br>'
                finalNoteBody += `${this.$custom.textAreaHtml(treatment.operative_note)}`
              }

              if (treatment.procedure_id === 178) {
                finalNoteBody += this.buildAmnioGraftNote({ woundTreatment, wound })
              }

            } else if (dermTreatment) {
              if (dermTreatment.topical_agent
                || dermTreatment.topical_agent_strength
                || dermTreatment.secondary_topical_agent
                || dermTreatment.secondary_topical_agent_strength
              ) {
                finalNoteBody += '<br><br><b>TOPICAL AGENTS USED:</b><br>'
                const topicalAgents = [
                  dermTreatment.topical_agent
                  + (dermTreatment.topical_agent_strength
                    ? ` (strength: ${dermTreatment.topical_agent_strength})`
                    : ' '),
                  dermTreatment.secondary_topical_agent
                  + (dermTreatment.secondary_topical_agent_strength
                    ? ` (strength: ${dermTreatment.secondary_topical_agent_strength})`
                    : ' '),
                ].filter(x => x !== null && x !== 'null ')
                finalNoteBody += `${this.$custom.toListCommaAnd(topicalAgents, null, 'none')}`
              }
              if (procedureId !== 999 && treatment.operative_note) {
                finalNoteBody += '<br><br><b>PROCEDURAL NOTE:</b><br>'
                finalNoteBody += `${this.$custom.textAreaHtml(treatment.operative_note)}`
              }
            }
          }
        })
      }
      this.final_note.body = this.$custom.clean(finalNoteBody, 'Wound Body', false)
    },

    buildAmnioGraftNote({ woundTreatment, wound }) {
      let note = '<br><br><b>AMNIOTIC TISSUE GRAFT:</b>'
      note += '<br>An amniotic tissue graft was applied to this wound. The pertinent ' +
        'information regarding this is as follows:'
      const indent = '&emsp;'
      note += '<br><br>Product information:<br>'

      // ─── Amnio‑graft table ────────────────────────────────────────────────────────
      note += `
        <div data-section="AMNIO_GRAFT_TABLE_START"></div>
        <table width="50%" border="1" cellspacing="0">
          <tr>
            <td style="padding:6px">Graft size&nbsp;(cm)</td>
            <td style="padding:6px">Quantity</td>
            <td style="padding:6px">Total area&nbsp;(cm²)</td>
          </tr>
        `

      let totalArea = 0

      if (Array.isArray(woundTreatment.amnio_grafts)) {
        woundTreatment.amnio_grafts.forEach((graft, idx) => {
          if (!graft.deleted) {
            totalArea += Number(graft.total_area)

            note += `
              <tr data-index="${idx}">
              <td style="padding:6px">${graft.graft_length} x ${graft.graft_width}</td>
              <td style="padding:6px">${graft.graft_qty}</td>
              <td style="padding:6px">${graft.total_area}</td>
              </tr>
            `
          }
        })
      }

      totalArea = Number(totalArea).toFixed(1)

      note += `
        <tr>
          <td style="padding:6px" colspan="2">Total</td>
          <td style="padding:6px">${totalArea}</td>
        </tr>
      </table>
      <div data-section="AMNIO_GRAFT_TABLE_END"></div>
      `
      // ────────────────────────────────────────────────────────────────────────────

      // Product used sentence
      const pluralVerb = totalArea === "1.0" ? 'was' : 'were'
      const amnioData = woundTreatment.wound_treatments_amnios
      const productName = amnioData.product_used
      const productWasted = amnioData.product_wasted
      const woundLength = parseFloat(amnioData.dim_length) || 0
      const woundWidth = parseFloat(amnioData.dim_width) || 0
      const woundArea = +(woundLength * woundWidth).toFixed(1)

      let sentence = `A total of ${totalArea} cm² of amniotic tissue grafts ${pluralVerb}`
      sentence += ` applied to the wound. The product used was ${productName},`

      if (productWasted === 'No product was wasted.') {
        sentence += ' with no product wasted.'
      } else if (productWasted === 'There was excess product which was folded into the wound.') {
        const quantityWasted = +(totalArea - woundArea).toFixed(1)
        if (quantityWasted > 0) {
          sentence += ` with ${quantityWasted} cm² of excess product folded into the wound.`
        } else {
          sentence += ' with some excess product folded into the wound.'
        }
      } else if (productWasted === 'Some product was wasted.') {
        const manualWaste = parseFloat(amnioData.quantity_wasted || 0).toFixed(1)
        const reason = amnioData.reason_wasted
        sentence += ` with ${manualWaste} cm² of graft product wasted due to: ${reason}`
      }

      note += `<br><br>${indent}<b><u>${sentence}.</u></b>`

      // Secondary procedure
      const secondaryProcedure = amnioData.secondary_procedure
      if (secondaryProcedure) {
        const procedureDescription = secondaryProcedure.substring(6).trim()
        note += `<br><br>Initial procedure performed for preparation of amniotic tissue graft placement:<br>`
        note += `${indent}${procedureDescription}`

        if (procedureDescription.toLowerCase().includes('debrid')) {
          const preparedSynonyms = ['prepared', 'primed']
          const treatmentSynonyms = ['treatment', 'administration', 'application']
          const removeSynonyms = ['remove', 'clear away', 'cut out', 'eliminate']
          const subsequentSynonyms = ['Subsequently', 'Next', 'Then', 'Afterwards', 'Following this']
          const cleanseSynonyms = ['cleansed', 'cleaned', 'sanitized', 'purified']
          const usingSynonyms = ['using', 'utilizing', 'by means of']
          const rand = arr => arr[Math.floor(Math.random() * arr.length)]

          note += `<br>${indent}The wound bed was ${rand(preparedSynonyms)} for graft ${rand(treatmentSynonyms)} by sharp debridement as described above to ${rand(removeSynonyms)} all devitalized tissue. `
          note += `${rand(subsequentSynonyms)}, the base of the wound was ${rand(cleanseSynonyms)} ${rand(usingSynonyms)} normal saline-moistened gauze to ${rand(removeSynonyms)} any remaining debris from the surface of the wound bed.<br><br>`
        }
      }

      const rawTime = amnioData.time_of_service
      const timeOfService = dayjs(rawTime, 'HH:mm').format('h:mm A')
      note += `<br><br>Time of service: ${timeOfService}`

      // Add duration
      note += `<br><br>Wound duration:<br>`
      note += `${indent}This wound has been present on the patient and non-healing/poor healing`
      note += ` for an amount of time exceeding 4 weeks, and therefore qualifies for the guidelines`
      note += ` recommended for amniotic tissue grafts.`

      // Add wound treatment summary
      const summary = this.woundAtgTreatmentData.find(item => item.woundId === wound.id)
      if (summary) {
        const { first_treatment, fourth_week_treatment } = summary

        if (first_treatment) {
          const {
            visit_date,
            wound_stage: firstStage,
            primary_length: firstLength,
            primary_width: firstWidth,
            primary_depth: firstDepth,
          } = first_treatment

          const firstDate = dayjs(visit_date).format('MM/DD/YYYY')
          note += `  This wound was first treated on ${firstDate}. At that time,`
          note += ` it had dimensions of ${firstLength} cm L x ${firstWidth} cm W x ${firstDepth}`

          if (!isNaN(parseFloat(firstDepth))) {
            note += ' cm'
          }
          note += ' D'

          if (firstStage) {
            note += ` and a staging of ${firstStage}`
          }
          note += '.'
        }

        if (fourth_week_treatment) {
          const {
            visit_date,
            wound_stage: fourthStage,
            primary_length: fourthLength,
            primary_width: fourthWidth,
            primary_depth: fourthDepth,
          } = fourth_week_treatment

          const fourthDate = dayjs(visit_date).format('MM/DD/YYYY')
          note += `  The first treatment made on this wound on or after four weeks after its initial`
          note += ` treatment occurred on ${fourthDate}. At that time, it had`
          note += ` dimensions of ${fourthLength} cm L x ${fourthWidth} cm W x ${fourthDepth}`

          if (!isNaN(parseFloat(fourthDepth))) {
            note += ' cm'
          }
          note += ' D'

          if (fourthStage) {
            note += ` and a staging of ${fourthStage}`
          }
          note += '.'
        }
      }

      // Append statement of medical necessity
      note += '<br><br>Statement of medical necessity: '
      note += woundTreatment.wound_treatments_amnios.reason_required
      // Append wound thickness statement
      note += '<br><br>Wound thickness: this wound is considered to be a '
      note += woundTreatment.wound_treatments_amnios.thickness.toLowerCase()
      note += '.'

      // If VLU Duplex Ultrasound checkbox was selected
      if (woundTreatment.wound_treatments_amnios.is_duplex_ultrasound_performed) {
        note += '<br><br>This wound is considered a venous leg ulcer (VLU) with a CEAP ' +
          'classification of C6. A duplex ultrasound was unavailable to be performed ' +
          'in-facility, but a physical exam did confirm the presence of venous disease.'
      }

      // Diabetes management
      note += `<br><br>Diabetes management:<br>${indent}`
      if (woundTreatment.wound_treatments_amnios.diabetes === 'Patient has diabetes') {
        note += `This patient has a diagnosis of diabetes`

        const hasType1 = this.encounter.medical_histories.some(x => x.id === 12)
          || this.$custom.strContains(this.encounter.medical_histories_other, 'diabetes type 1')
          || this.$custom.strContains(this.encounter.risk_factors_other, 'diabetes type 1')

        const hasType2 = this.encounter.medical_histories.some(x => x.id === 13)
          || this.$custom.strContains(this.encounter.medical_histories_other, 'diabetes type 2')
          || this.$custom.strContains(this.encounter.risk_factors_other, 'diabetes type 2')

        if (hasType1) {
          note += ' type 1'
        } else if (hasType2) {
          note += ' type 2'
        }

        note += ' which is being managed by: '
        note += woundTreatment.wound_treatments_amnios.diabetes_management + '.'
        if (this.validA1cEntry) {
          note += `<br><br>Most recent hemoglobin A1c (HbA1c) level: ${this.validA1cEntry.level}`
          note += ` (measured on ${dayjs(this.validA1cEntry.date).format('MM/DD/YYYY')}).`
        }

      } else {
        note += 'Patient is recorded as not being diabetic.'
      }

      // Tobacco use
      note += `<br><br>Tobacco use:<br>${indent}`

      if (woundTreatment.wound_treatments_amnios.tobacco_cessation === 'Patient is a non-smoker') {
        note += 'Patient is a non-smoker.'
      } else if (woundTreatment.wound_treatments_amnios.tobacco_cessation === 'Tobacco cessation counseling performed') {
        const openings = ['At', 'During']
        const visits = ['today\'s visit', 'the visit today', 'the visit', 'this visit']
        const verbs = ['discussed', 'talked about', 'conversed about']
        const subjects = ['the patient\'s', 'this patient\'s', 'patient\'s']
        const habits = [
          'tobacco habit',
          'tobacco usage',
          'usage of tobacco',
          'habit of tobacco usage',
          'habit of using tobacco'
        ]
        const importance = [
          'the importance',
          'how important it is',
          'how it is important',
          'the criticality',
          'how critical it is',
          'how it is critical'
        ]
        const actions = ['not smoke', 'do not smoke', 'should not smoke']
        const graftTime = [
          'during the time that',
          'for the duration of time that',
          'while'
        ]
        const graftType = ['amniotic tissue graft', 'amniotic graft']
        const patientSaid = [
          'The patient stated',
          'The patient indicated',
          'Patient stated',
          'Patient indicated'
        ]
        const pronouns = ['they', 'that they']
        const understandings = [
          'understand',
          'comprehend',
          'apprehend'
        ]
        const awareness = [
          'aware',
          'conscious',
          'informed',
          'mindful',
          'cognizant'
        ]
        const riskConnect = [
          'associated with',
          'connected to',
          'connected with',
          'related to',
          'related with'
        ]
        const complications = [
          'complications',
          'ill effects',
          'adverse effects',
          'detrimental effects',
          'negative effects'
        ]
        const showTobacco = Math.random() < 0.5 ? ' or use tobacco' : ''

        note += `${openings[Math.floor(Math.random() * openings.length)]} `
        note += `${visits[Math.floor(Math.random() * visits.length)]}, we `
        note += `${verbs[Math.floor(Math.random() * verbs.length)]} `
        note += `${subjects[Math.floor(Math.random() * subjects.length)]} `
        note += `${habits[Math.floor(Math.random() * habits.length)]}, and `
        note += `${importance[Math.floor(Math.random() * importance.length)]} that they `
        note += `${actions[Math.floor(Math.random() * actions.length)]}${showTobacco} `
        note += `${graftTime[Math.floor(Math.random() * graftTime.length)]} the `
        note += `${graftType[Math.floor(Math.random() * graftType.length)]} is applied. `
        note += `${patientSaid[Math.floor(Math.random() * patientSaid.length)]} `
        note += `${pronouns[Math.floor(Math.random() * pronouns.length)]} `
        note += `${understandings[Math.floor(Math.random() * understandings.length)]} and is `
        note += `${awareness[Math.floor(Math.random() * awareness.length)]} of risk factors `
        note += `${riskConnect[Math.floor(Math.random() * riskConnect.length)]} `
        note += `tissue graft failure and ${complications[Math.floor(Math.random() * complications.length)]} `
        note += `if they smoke at any time.`
      }

      // Osteomyelitis Section
      if (woundTreatment.wound_treatments_amnios.is_no_osteomyelitis) {
        note += '<br><br>Osteomyelitis: the patient does not have osteomyelitis at this time.'
      }

      // Blood circulation adequacy
      if (woundTreatment.wound_treatments_amnios.is_adequate_circulation) {
        note += '<br><br>Blood circulation adequacy: this patient is being documented as exhibiting ' +
          'adequate blood circulation at this time.  This includes measuring an ankle-brachial ' +
          'index/ABI of 0.6 or greater, as well as a toe pressure of at least 30 mmHg.'
      }

      // Wound site exudate
      if (woundTreatment.wound_treatments_amnios.is_no_exudate) {
        note += '<br><br>Wound site exudate: upon examination, this wound exhibits a normal amount of ' +
          'exudate with no unusual odor or signs of infection.'
      }

      // Tissue graft operative note
      note += `<br><br>Tissue graft operative note:<br>${indent}`
      // Check if there's a secondary procedure related to debridement
      const isDebridement =
        secondaryProcedure && secondaryProcedure.toLowerCase().includes('debrid')

      if (isDebridement) {
        note += `Please note that, as stated above, an initial debridement procedure was performed ` +
          `in preparation for the amniotic tissue application. The main operative note above describes ` +
          `the surgical procedure related to this debridement in further detail.<br>${indent}`
      }
      note += `${woundTreatment.wound_treatments_amnios.application_notes}`

      return note
    },
  },
}
